import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import {
  XrtAttributeFilter,
  XrtSearchQueryHeader,
  XrtCustomerSearchRqst,
  ElasticSearchApiService,
  XrtCustomerSearchFilter,
} from '@xpo-ltl/sdk-elasticsearch';
import { FormatValidationService } from '@xpo-ltl/common-services';

import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';

import { RfcDialogFormFields } from '@shared/enums';
import { EditRfcDialogDataInterface } from '@shared/models';

import { madCodeValidator, RemoteMadcodeValidator } from '@shared/validators/madcode-validator/madcode.validator';
import { AddRfcDialogData } from '@shared/models/add-rfc-dialog-data';

export interface SearchMadCdResponse {
  header: any;
  result: any[];
  madcode: string;
}

@Component({
  selector: 'app-edit-rfc-dialog',
  templateUrl: './edit-rfc-dialog.component.html',
  styleUrls: ['./edit-rfc-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EditRfcDialogComponent implements OnInit, OnDestroy {

  editRfcFormFields = RfcDialogFormFields;
  formGroup: FormGroup;
  customerNbr: number;

  private unsubscriber = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private elasticsearchService: ElasticSearchApiService,
    private dialogRef: MatDialogRef<EditRfcDialogComponent>,
    private remoteMadcodeValidator: RemoteMadcodeValidator,
    private formatValidationService: FormatValidationService,
    @Inject(MAT_DIALOG_DATA) public data: EditRfcDialogDataInterface
  ) {}

  ngOnInit() {
    const existingMadCodes = new AddRfcDialogData(this.data.existingMadCodes);

    this.formGroup = this.formBuilder.group({
      [RfcDialogFormFields.MADCODE]: [
        this.data.madCode,
        {
          validators: [
            Validators.required,
            madCodeValidator(this.formatValidationService, existingMadCodes, this.data.madCode)
          ],
          asyncValidators: [this.remoteMadcodeValidator.existingMadcodeValidator()],
          updateOn: 'blur',
        },
      ],
      [RfcDialogFormFields.NAME]: [this.data.name, Validators.required],
      [RfcDialogFormFields.ADDRESS]: [this.data.address, Validators.required],
      [RfcDialogFormFields.RFC_NUMBER]: [this.data.rfcNbr, [Validators.required, Validators.maxLength(20)]],
    });
    this.formGroup.markAllAsTouched();

    this.customerNbr = this.data.customerNbr;

    this.onValueChanges();
  }

  hasError(error: string, field: RfcDialogFormFields): boolean {
    return this.formGroup.get(field).hasError(error) && this.formGroup.get(field).touched;
  }

  onValueChanges(): void {
    this.formGroup.get(RfcDialogFormFields.MADCODE).statusChanges.subscribe((res) => {
      if (res === 'VALID') {
        this.xrtCustomerSearch(this.formGroup.get(RfcDialogFormFields.MADCODE).value).subscribe((customerResult) => {
          if (customerResult && customerResult.result.length) {
            const customer = customerResult.result[0];
            this.customerNbr = customer.acctInstId;
            const address = `${customer.addressLine}, ${customer.cityName} (${customer.stateCd})`;
            this.formGroup.get(RfcDialogFormFields.NAME).setValue(customer.name1);
            this.formGroup.get(RfcDialogFormFields.ADDRESS).setValue(address);
          } else {
            this.formGroup.get(RfcDialogFormFields.NAME).setValue(null);
            this.formGroup.get(RfcDialogFormFields.ADDRESS).setValue(null);
          }
        });
      } else {
        this.formGroup.get(RfcDialogFormFields.NAME).setValue(null);
        this.formGroup.get(RfcDialogFormFields.ADDRESS).setValue(null);
      }
    });
  }

  private xrtCustomerSearch(madcode: string): Observable<any> {
    const options = new XrtCustomerSearchRqst();
    options.header = new XrtSearchQueryHeader();
    options.filter = new XrtCustomerSearchFilter();
    options.header.page = 1;
    options.header.pageSize = 1;
    options.header.sortExpressions = [{ IsDescendingSort: true, Column: 'seqNbr', direction: 'asc' }];
    options.filter.acctMadCd = new XrtAttributeFilter();
    options.filter.acctMadCd.equals = madcode;
    return this.elasticsearchService.xrtCustomerSearch(options);
  }

  ngOnDestroy() {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  closeClicked() {
    this.dialogRef.close();
  }

  saveClicked() {
    if (this.formGroup.valid) {
      const rfcData = this.formGroup.getRawValue();
      rfcData.customerNbr = this.customerNbr;
      rfcData.isInvoice = this.data.isInvoice;
      rfcData.pros = this.data.pros.map(pro => pro.shipmentInstId);
      this.dialogRef.close(rfcData);
    }
  }
}
